import { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import {
  calculateNiiStrategyType,
  calculateRequiredCoverage,
  ConsequenceOfFailure,
  consequenceOfFailureSchema,
  deriveNiiJustification,
  getEffectiveStrategyType,
  InspectionStrategy,
  NiiStrategy,
  sanitizeNiiJustification,
} from 'features/libra/stepComponents/zoneAnalysis/niiStrategy'
import {
  DamageMechanism,
  damageMechanismSchema,
  GEOMETRY_OPTIONS,
  MATERIAL_OPTIONS,
  ZoneAnalysisConfig,
  ZoneParams,
  type ZoneTabKey as ZoneEditorTabKey,
  ZoneTabPrefillMeta,
} from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import {
  affectsZoneSurfaceArea,
  deriveZoneSurfaceArea,
} from 'features/libra/stepComponents/zoneAnalysis/zoneSurfaceArea'
import { z } from 'zod'

/**
 * Parsing + merge for per-tab zone questionnaire AI prefill, served through
 * the standard assess endpoint (`POST /assess/section` with a zone-tab
 * section's `executionStrategy: "zone-tab-prefill"`).
 *
 * The backend emits a per-tab FRAGMENT — `{zone_id, zone_tab, zone_key,
 * fields: {f: {value, citation}}, component_match, rule_notes}` — never a
 * whole zone, so a prefill can only write its own tab's fields and concurrent
 * SME edits on other tabs survive. `value: null` is contractual for "the AI
 * could not source this" and is surfaced (`unfound`), never written over an
 * existing SME value.
 */

/** Section ids of the zone-tab agent sections in libra-workflow. */
export const ZONE_TAB_SECTION_IDS = {
  basic_info: 'zone_tab_basic_info',
  design_function: 'zone_tab_design_function',
  damage_mechanisms: 'zone_tab_damage_mechanisms',
  nii_strategy: 'zone_tab_nii_strategy',
  risk_assessment: 'zone_tab_risk_assessment',
  consequence_of_failure: 'cof_section',
  zone_breakdown: 'zone_breakdown_section',
} as const

export type ZoneTabKey = keyof typeof ZONE_TAB_SECTION_IDS

/** The per-zone tabs, in card tab order — also the "Pre-fill all" run order:
 * content tabs first, then strategy (whose answers gate the Risk Assessment
 * panel), then risk. */
export const ZONE_CARD_TABS = [
  'basic_info',
  'design_function',
  'damage_mechanisms',
  'nii_strategy',
  'risk_assessment',
] as const satisfies readonly ZoneTabKey[]

export type ZoneCardTabKey = (typeof ZONE_CARD_TABS)[number]

/** Maps the editor's short UI keys to the backend zone-tab ids. */
export const ZONE_EDITOR_TAB_TO_PREFILL_TAB: Record<
  ZoneEditorTabKey,
  ZoneCardTabKey
> = {
  basic: 'basic_info',
  design: 'design_function',
  damage: 'damage_mechanisms',
  strategy: 'nii_strategy',
  rba: 'risk_assessment',
}

/** Resolve a host step's visible editor tabs to the exact prefills it may run. */
export function getVisibleZonePrefillTabs(
  visibleTabs: readonly ZoneEditorTabKey[]
): ZoneCardTabKey[] {
  return visibleTabs.map((tab) => ZONE_EDITOR_TAB_TO_PREFILL_TAB[tab])
}

export type ZoneTabAgentPermissions = Partial<Record<ZoneCardTabKey, string[]>>

export interface ZonePrefillDerivationContext {
  cofModifier: number
  confidence: 'low' | 'med' | 'high'
}

/**
 * Array-table assessors use the submitted rows as their output shape. An
 * empty damage-mechanism array therefore gives the assessor no row to fill.
 * Supply one transient blank row in the request only; the returned domain
 * rows replace it, and this template is never applied to or saved with the
 * zone.
 */
export function buildZoneTabPrefillInputs(
  zone: ZoneParams,
  tab: ZoneCardTabKey
): Record<string, unknown> {
  const needsDamageMechanismTemplate =
    tab === 'damage_mechanisms' && zone.damage_mechanisms.length === 0

  return {
    zone_id: zone.id,
    zone: needsDamageMechanismTemplate
      ? {
          ...zone,
          damage_mechanisms: [
            {
              name: '',
              rate_unit: 'mm/y',
              justification: '',
            },
          ],
        }
      : zone,
  }
}

/** Which ZoneParams key each tab's fields nest under (null = zone top level). */
const TAB_ZONE_KEY: Record<ZoneTabKey, 'nii_strategy' | 'rba_info' | null> = {
  basic_info: null,
  design_function: null,
  damage_mechanisms: null,
  nii_strategy: 'nii_strategy',
  risk_assessment: 'rba_info',
  consequence_of_failure: null,
  zone_breakdown: null,
}

const fieldEnvelopeSchema = z.object({
  value: z.unknown().nullish(),
  citation: z.unknown().nullish(),
})

export const zoneTabFragmentSchema = z.object({
  zone_id: z.string(),
  zone_tab: z.string(),
  zone_key: z.string().nullish(),
  fields: z.record(z.string(), fieldEnvelopeSchema),
  component_match: z
    .object({
      component_entry_id: z.string().nullish(),
      component_name: z.string().nullish(),
      confidence: z.enum(['low', 'medium', 'high']).nullish(),
      justification: z.string().nullish(),
    })
    .nullish(),
  rule_notes: z.array(z.string()).default([]),
  documents_used: z.array(z.string()).default([]),
})

export type ZoneTabFragment = z.infer<typeof zoneTabFragmentSchema>

/**
 * Extract the tab fragment from an assess `field_values` payload.
 * Returns null (never throws) when the response doesn't carry one.
 */
export function parseZoneTabFragment(
  fieldValues: FieldValues
): ZoneTabFragment | null {
  const raw = fieldValues.zones?.value
  const parsed = zoneTabFragmentSchema.safeParse(raw)
  if (!parsed.success) {
    console.error('Zone tab fragment parse error:', parsed.error)
    return null
  }
  return parsed.data
}

// Canonical option value sets, derived from the panel's dropdowns (single
// sourced in zoneAnalysisApi) so a canonicalized value can never fall outside
// what the select actually offers.
const MATERIAL_VALUES = MATERIAL_OPTIONS.map((o) => o.value)
const GEOMETRY_VALUES = GEOMETRY_OPTIONS.map((o) => o.value)

/** Free-text → canonical Material code, keyed off MATERIAL_OPTIONS. */
const MATERIAL_CODES: Array<[RegExp, string]> = [
  [/cra[\s_-]*clad|clad/i, 'CRA_CLAD'],
  [/duplex/i, 'DUPLEX'],
  [/inconel/i, 'INCONEL'],
  [/monel/i, 'MONEL'],
  [/titanium/i, 'TITANIUM'],
  [/316/, 'SS316'],
  [/304/, 'SS304'],
  [/stainless|\bss\b/i, 'SS316'],
  [/\bcra\b|corrosion.resistant/i, 'CRA'],
  [/carbon|\bcs\b|sa[\s-]?516|sa[\s-]?106/i, 'CS'],
]

export function canonicalizeMaterial(raw: string): string {
  const s = raw.trim()
  const upper = s.toUpperCase()
  if ((MATERIAL_VALUES as readonly string[]).includes(upper)) return upper
  for (const [pattern, code] of MATERIAL_CODES) {
    if (pattern.test(s)) return code
  }
  // The Design panel is a constrained select, not free text. Preserve the
  // model's exact wording in its citation, while keeping the persisted value
  // inside the option contract.
  return 'OTHER'
}

/** Free-text → canonical Geometry code, keyed off GEOMETRY_OPTIONS. */
export function canonicalizeGeometry(raw: string): string {
  const s = raw.trim()
  const upper = s.toUpperCase()
  if ((GEOMETRY_VALUES as readonly string[]).includes(upper)) return upper
  if (/cylind|shell band/i.test(s)) return 'CYLINDRICAL'
  if (/spher/i.test(s)) return 'SPHERICAL'
  if (/conic|cone/i.test(s)) return 'CONICAL'
  if (/ellips|dished|head/i.test(s)) return 'ELLIPSOIDAL'
  if (/rect/i.test(s)) return 'RECTANGLE'
  return 'CUSTOM'
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

type ComponentMatchLike = {
  component_name?: string | null
  component_entry_id?: string | null
} | null

/**
 * An APM citation may arrive carrying only its source marker
 * (`doc: "APM component data"`); the human-readable component + field come from
 * the run's component match. Fill those in from the match (and the field key)
 * so the citation chip never renders blanks. Non-APM (or non-object) citations
 * pass through untouched. Shared by ingestion (applyFragmentToZone) and render
 * (AiPrefillFieldState), so older saved runs get the same treatment.
 */
export function completeApmCitation(
  citation: unknown,
  componentMatch: ComponentMatchLike | undefined,
  field: string
): unknown {
  if (!isRecord(citation) || citation.doc !== 'APM component data') {
    return citation
  }
  return {
    ...citation,
    component:
      firstNonEmptyString(citation.component) ??
      componentMatch?.component_name ??
      componentMatch?.component_entry_id ??
      'Component data',
    field: firstNonEmptyString(citation.field) ?? field,
  }
}

function firstNonEmptyString(...values: unknown[]): string | undefined {
  return values.find(
    (value): value is string =>
      typeof value === 'string' && value.trim().length > 0
  )
}

function normalizeSusceptibility(value: unknown): unknown {
  if (typeof value !== 'string') return value
  const normalized = value.trim().toLowerCase()
  return ['low', 'medium', 'high'].includes(normalized) ? normalized : undefined
}

function normalizeRateValue(value: unknown): unknown {
  if (typeof value === 'number') return value
  if (typeof value !== 'string') return value
  const parsed = Number.parseFloat(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

/**
 * Accept the row spellings emitted by both the current zone-prefill contract
 * and older prompts. The table persists `name` / `rate_value` / `rate_unit`,
 * while older responses used `damage_mechanism`, `mechanism`, or `rate`.
 */
function normalizeDamageMechanismRows(value: unknown): DamageMechanism[] {
  let rows: unknown[]
  if (Array.isArray(value)) {
    rows = value
  } else if (isRecord(value) && Array.isArray(value.damage_mechanisms)) {
    rows = value.damage_mechanisms
  } else if (isRecord(value)) {
    rows = [value]
  } else {
    return []
  }

  return rows.flatMap((row) => {
    if (!isRecord(row)) return []

    const nestedRate = isRecord(row.rate) ? row.rate : undefined
    const name = firstNonEmptyString(
      row.name,
      row.damage_mechanism,
      row.mechanism
    )
    if (!name) return []

    const candidate = Object.fromEntries(
      Object.entries({
        ...row,
        name,
        rate_value: normalizeRateValue(
          row.rate_value ?? row.corrosion_rate ?? nestedRate?.value ?? row.rate
        ),
        rate_unit: row.rate_unit ?? nestedRate?.unit ?? row.unit ?? undefined,
        susceptibility: normalizeSusceptibility(
          row.susceptibility ?? row.rating
        ),
        justification:
          row.justification ?? row.rationale ?? row.reason ?? undefined,
      }).filter(([, entry]) => entry !== null && entry !== undefined)
    )
    const parsed = damageMechanismSchema.safeParse(candidate)
    return parsed.success ? [parsed.data] : []
  })
}

/**
 * The single definition of "this prefill field holds nothing": null/undefined,
 * a blank string, or an empty array. Shared by fragment ingestion and by the
 * panels' `AiPrefillFieldState empty={…}` props so "AI suggested" vs "needs
 * input" is decided the same way everywhere. Callers with a derived/effective
 * value (geometry area & height, which treat 0 as "not measured") gate on that
 * themselves — this operates on the raw stored field.
 */
export function isMissingPrefillValue(field: string, value: unknown): boolean {
  if (value === null || value === undefined) return true
  if (typeof value === 'string' && value.trim().length === 0) return true
  if (Array.isArray(value) && value.length === 0) return true
  // A physical surface area of zero means it could not be calculated; it
  // must not make the tab look complete or feed the coverage calculation.
  if (field === 'total_surface_area_sq_m' && value === 0) return true
  return false
}

/**
 * Merge a tab fragment into the zone, returning the `Partial<ZoneParams>`
 * update for the card's onSave. Only non-null AI values are written; null
 * values are recorded as `unfound` so the UI can say "AI could not find
 * this" instead of silently clearing SME work. The zone's `ai_prefill[tab]`
 * bookkeeping ("AI suggested until touched") is replaced wholesale — a
 * re-run supersedes the previous run's provenance.
 */
export function applyFragmentToZone(
  zone: ZoneParams,
  fragment: ZoneTabFragment
): Partial<ZoneParams> {
  const tab = fragment.zone_tab as ZoneTabKey
  // Trust the backend's zone_key — it derives from the one nested ontology
  // (gong-agent #54 B2), so it's the single source of the nesting truth. Fall
  // back to the local tab→key map only when the backend omits it (stale
  // service) or names a key the frontend doesn't nest under.
  const zoneKey: 'nii_strategy' | 'rba_info' | null =
    fragment.zone_key === 'nii_strategy' || fragment.zone_key === 'rba_info'
      ? fragment.zone_key
      : fragment.zone_key === null
        ? null
        : (TAB_ZONE_KEY[tab] ?? null)

  const filled: Record<string, unknown> = {}
  const filledFields: string[] = []
  const unfound: string[] = []
  for (const [field, envelope] of Object.entries(fragment.fields)) {
    let value =
      field === 'damage_mechanisms'
        ? normalizeDamageMechanismRows(envelope.value)
        : envelope.value
    if (
      field.toLowerCase().includes('justification') &&
      typeof value === 'string'
    ) {
      value = sanitizeNiiJustification(value)
    }
    // Null AND empty-list both mean "the AI produced nothing here" — an []
    // written into e.g. zone_recommendations would look silently skipped, so
    // it is surfaced as unfound instead.
    if (isMissingPrefillValue(field, value)) {
      unfound.push(field)
    } else {
      filled[field] = value
      filledFields.push(field)
    }
  }

  // material / geometry persist as the Design & Function select CODES (the
  // SME tickets store "CS" / "CUSTOM", not prose) — canonicalize whatever
  // spelling the model produced; unmappable text lands on OTHER / CUSTOM,
  // with the model's original wording still visible in the field's citation.
  if (typeof filled.material === 'string') {
    filled.material = canonicalizeMaterial(filled.material)
  }
  if (typeof filled.geometry === 'string') {
    filled.geometry = canonicalizeGeometry(filled.geometry)
  }

  const citations = Object.fromEntries(
    Object.entries(fragment.fields).flatMap(([field, envelope]) => {
      if (envelope.citation === null || envelope.citation === undefined) {
        return []
      }
      return [
        [
          field,
          completeApmCitation(
            envelope.citation,
            fragment.component_match,
            field
          ),
        ],
      ]
    })
  )

  // The zone→component match rides onto the zone so later tab runs (and the
  // SME) see it. When it REPLACES a different component the zone already had
  // (an SME confirmation, or a prior run's match), don't rebind silently:
  // record it in the tab's rule_notes so the change is auditable. CLAUDE.md #4
  // still trusts the producer (we apply the match), but #7/#9 require the audit
  // trail on an overwrite of SME-owned state.
  const matchedComponentId = fragment.component_match?.component_entry_id
  const rebindsComponent =
    !!matchedComponentId &&
    !!zone.componentId &&
    zone.componentId !== matchedComponentId
  const rebindNote = rebindsComponent
    ? `Component re-matched to ${
        fragment.component_match?.component_name ?? matchedComponentId
      }, replacing the component previously set on this zone — review before saving.`
    : undefined

  const meta: ZoneTabPrefillMeta = {
    generated_at: new Date().toISOString(),
    fields: filledFields,
    unfound,
    rule_notes: rebindNote
      ? [...fragment.rule_notes, rebindNote]
      : fragment.rule_notes,
    citations: Object.keys(citations).length ? citations : undefined,
    documents_used: fragment.documents_used.length
      ? fragment.documents_used
      : undefined,
    component_match: fragment.component_match ?? undefined,
  }

  const updates: Partial<ZoneParams> = {
    ai_prefill: { ...zone.ai_prefill, [tab]: meta },
  }

  if (zoneKey) {
    // Nested tab (nii_strategy / rba_info): merge over the existing object so
    // fields the AI couldn't source keep their SME values.
    updates[zoneKey] = {
      ...(zone[zoneKey] as object | undefined),
      ...filled,
    } as never
  } else {
    Object.assign(updates, filled)
  }

  if (matchedComponentId) {
    updates.componentId = matchedComponentId
  }

  return updates
}

/**
 * Derive the decision-tree outputs the NII Strategy panel maintains on human
 * edits — `strategy_type_calculated` and the top-level `justification` (the
 * last answered step's justification). The AI fragment carries only the three
 * answers, so an AI fill must run the same derivation or downstream tabs see
 * "Strategy Not Selected". Mirrors `NiiStrategyPanel.handleUpdate`.
 */
export function finalizeNiiStrategy(strategy: NiiStrategy): NiiStrategy {
  const finalized: NiiStrategy = {
    ...strategy,
    strategy_type_calculated: calculateNiiStrategyType(strategy),
    degradation_likely_justification: sanitizeNiiJustification(
      strategy.degradation_likely_justification
    ),
    degradation_predictable_justification: sanitizeNiiJustification(
      strategy.degradation_predictable_justification
    ),
    degradation_severity_justification: sanitizeNiiJustification(
      strategy.degradation_severity_justification
    ),
  }
  return { ...finalized, justification: deriveNiiJustification(finalized) }
}

/**
 * Apply one tab fragment AND run the derivations every prefill entry point
 * needs: finalize the NII decision-tree outputs when the strategy tab wrote (the
 * AI ships only the three answers), recompute the zone's surface area when the
 * design tab wrote a geometry input, and recompute coverage from the merged
 * zone. Single-sourced so the single-tab banner and the all-tabs runner can't
 * drift — they had each copied this sequence (review #7).
 *
 * Order matters: area feeds F_ZONE, so it must be derived BEFORE coverage.
 * Both are pure recomputes, so calling this per tab in the all-tabs loop leaves
 * the same final value as one call against the fully accumulated draft.
 */
export function applyTabFragmentWithDerivations(
  zone: ZoneParams,
  fragment: ZoneTabFragment,
  derivationContext?: ZonePrefillDerivationContext
): Partial<ZoneParams> {
  const updates = applyFragmentToZone(zone, fragment)
  if (updates.nii_strategy) {
    updates.nii_strategy = finalizeNiiStrategy(updates.nii_strategy)
  }
  // The AI supplies the calculator's INPUTS (diameter, length, basis, head
  // type, proportions); the area itself is arithmetic and is never taken from
  // the model. See ./zoneSurfaceArea.
  if (affectsZoneSurfaceArea(updates)) {
    Object.assign(updates, deriveZoneSurfaceArea({ ...zone, ...updates }))
  }
  if (derivationContext) {
    updates.coverage_calculation = calculateRequiredCoverage(
      { ...zone, ...updates },
      derivationContext.cofModifier,
      derivationContext.confidence
    )
  }
  return updates
}

/**
 * Run every zone-card tab's prefill sequentially against an ACCUMULATING
 * draft zone, so each tab sees the previous tabs' answers (the strategy tab
 * sees the AI damage mechanisms; the risk tab sees the AI strategy) and the
 * zone→component match from the first call rides `componentId` into the rest.
 *
 * Returns ONE merged `Partial<ZoneParams>` to apply atomically — applying
 * per-tab from a stale zone would clobber `ai_prefill` bookkeeping between
 * tabs. Per-tab failures are collected, never thrown: a dead tab leaves its
 * fields for the SME and the rest of the run proceeds.
 *
 * `postSection` is injected (section_id, inputs) → assess field_values, so
 * this stays a pure orchestration function.
 */
export async function runAllTabPrefills({
  zone,
  postSection,
  derivationContext,
  tabs = ZONE_CARD_TABS,
  onProgress,
}: {
  zone: ZoneParams
  postSection: (
    sectionId: string,
    inputs: Record<string, unknown>
  ) => Promise<FieldValues>
  derivationContext?: ZonePrefillDerivationContext
  tabs?: readonly ZoneCardTabKey[]
  onProgress?: (tab: ZoneCardTabKey) => void
}): Promise<{
  updates: Partial<ZoneParams>
  errors: Partial<Record<ZoneCardTabKey, string>>
}> {
  let draft: ZoneParams = { ...zone }
  const updates: Partial<ZoneParams> = {}
  const errors: Partial<Record<ZoneCardTabKey, string>> = {}

  for (const tab of tabs) {
    // Type C never has risk factors — mirror the panel's gate.
    if (
      tab === 'risk_assessment' &&
      getEffectiveStrategyType(draft.nii_strategy) === InspectionStrategy.C
    ) {
      continue
    }
    onProgress?.(tab)
    try {
      const fieldValues = await postSection(
        ZONE_TAB_SECTION_IDS[tab],
        buildZoneTabPrefillInputs(draft, tab)
      )
      const fragment = parseZoneTabFragment(fieldValues)
      if (!fragment) {
        errors[tab] = 'Unparseable AI response'
        continue
      }
      const tabUpdates = applyTabFragmentWithDerivations(
        draft,
        fragment,
        derivationContext
      )
      draft = { ...draft, ...tabUpdates }
      Object.assign(updates, tabUpdates)
    } catch (err: unknown) {
      errors[tab] =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? 'Request failed'
    }
  }

  // In the NII Zones host, coverage_calculation is set per tab and the last
  // successful tab's value is computed from the fully accumulated draft. Data
  // Gathering omits derivationContext, so early-tab prefills cannot stage NII
  // coverage using placeholder CoF/confidence values.
  return { updates, errors }
}

/**
 * "AI suggested until touched": given a human edit (`updates`), drop every
 * edited field from the zone's prefill bookkeeping so its AI treatment and
 * citation disappear. Returns the `ai_prefill` patch, or null when nothing
 * changes. Callers must NOT invoke this for AI-applied updates (they carry
 * `ai_prefill` themselves).
 */
export function clearTouchedPrefill(
  zone: ZoneParams,
  updates: Partial<ZoneParams>
): Pick<ZoneParams, 'ai_prefill'> | null {
  const prefill = zone.ai_prefill
  if (!prefill || 'ai_prefill' in updates) return null

  let changed = false
  const next: NonNullable<ZoneParams['ai_prefill']> = {}
  for (const [tab, meta] of Object.entries(prefill)) {
    const zoneKey = TAB_ZONE_KEY[tab as ZoneTabKey]
    let touched: Set<string>
    if (zoneKey && updates[zoneKey]) {
      // Nested tab: a field counts as touched when its value differs from
      // what the zone currently holds.
      const before = (zone[zoneKey] ?? {}) as Record<string, unknown>
      const after = updates[zoneKey] as Record<string, unknown>
      touched = new Set(
        Object.keys(after).filter((k) => after[k] !== before[k])
      )
    } else {
      touched = new Set(Object.keys(updates))
      // The Design panel persists human overrides separately from the
      // source/calculated field. Treat the override as ownership of the
      // visible field so an SME-edited area does not stay blue as AI-owned.
      if (touched.has('height_mm_override')) touched.add('height_mm')
      if (touched.has('length_mm_override')) touched.add('length_mm')
      if (touched.has('total_surface_area_sq_m_override')) {
        touched.add('total_surface_area_sq_m')
      }
      // A geometry edit recomputes the area alongside itself; the recomputed
      // field is derived, not the human's edit, so it must not count as
      // touching (and un-highlighting) an AI-owned field.
      touched.delete('surface_area_calculated_sq_m')
      touched.delete('coverage_calculation')
    }
    const fields = meta.fields.filter((f) => !touched.has(f))
    const unfound = meta.unfound.filter((f) => !touched.has(f))
    if (
      fields.length !== meta.fields.length ||
      unfound.length !== meta.unfound.length
    ) {
      changed = true
    }
    next[tab] = { ...meta, fields, unfound }
  }
  return changed ? { ai_prefill: next } : null
}

/**
 * Extract the CoF value from a `cof_section` assess response. The backend
 * ships `{value: {level, justification}, citation}` under the standard
 * `consequence_of_failure` field; a null level means "no numeric SHE CoF in
 * APM" and is returned as `null` so the caller can tell the user instead of
 * writing a guess.
 */
export function parseCofPrefill(
  fieldValues: FieldValues
): ConsequenceOfFailure | null {
  const raw = fieldValues.consequence_of_failure?.value
  if (!raw || typeof raw !== 'object') return null
  const candidate = raw as { level?: unknown; justification?: unknown }
  if (candidate.level === null || candidate.level === undefined) return null
  const parsed = consequenceOfFailureSchema.safeParse(candidate)
  return parsed.success ? parsed.data : null
}

/**
 * Extract the AI zone-breakdown justification from a `zone_breakdown_section`
 * assess response. Null when the model produced nothing.
 */
export function parseBreakdownPrefill(fieldValues: FieldValues): string | null {
  const raw = fieldValues.zone_breakdown_justification?.value
  return typeof raw === 'string' && raw.trim() ? raw : null
}

/**
 * Workflow-author-scoped extra permissions for a zone-tab AI action, read
 * from that tab's section config — the same per-section gating every other
 * agent section honors (see `getZoneAgentPermissions`).
 */
export function getZoneTabAgentPermissions(
  config: ZoneAnalysisConfig,
  tab: ZoneTabKey
): string[] {
  return (
    config.sections?.find((s) => s.id === ZONE_TAB_SECTION_IDS[tab])
      ?.agentPermissions ?? []
  )
}
